import {
  ERROR_CORRECTION_LEVELS,
  INNER_EYE_STYLES,
  MODULE_STYLES,
  OUTER_EYE_STYLES,
  type ErrorCorrectionLevel,
  type InnerEyeStyle,
  type ModuleStyle,
  type OuterEyeStyle,
  type StyleOption,
} from '../domain/types';

/**
 * UI copy for every style.
 *
 * These are `Record`s keyed by the style union rather than free-standing
 * arrays, so adding a style to the union without giving it a label is a
 * compile error. The old arrays and the renderer's switch statements drifted
 * apart silently — several advertised styles had no implementation and one
 * implementation called a function that did not exist.
 */
interface Copy {
  readonly label: string;
  readonly description: string;
}

/**
 * Eye styles that keep the finder pattern's 1:1:3:1:1 dark/light ratio — the
 * structure a decoder locks onto. Verified by decoding a rendered symbol with
 * jsQR for every style; see `eyes.scannability.test.ts`.
 *
 * Styles outside these sets still render, but are decorative only.
 */
export const SCANNER_SAFE_OUTER_EYES = [
  'square',
  'rounded',
  'circle',
  'rounded_top_left',
  'rounded_top_right',
  'rounded_bottom_left',
  'rounded_bottom_right',
  'diagonal',
  'shield',
] as const satisfies readonly OuterEyeStyle[];

export const SCANNER_SAFE_INNER_EYES = [
  'square',
  'rounded',
  'circle',
] as const satisfies readonly InnerEyeStyle[];

export function isScannerSafeOuterEye(style: OuterEyeStyle): boolean {
  return (SCANNER_SAFE_OUTER_EYES as readonly OuterEyeStyle[]).includes(style);
}

export function isScannerSafeInnerEye(style: InnerEyeStyle): boolean {
  return (SCANNER_SAFE_INNER_EYES as readonly InnerEyeStyle[]).includes(style);
}

const toOptions = <T extends string>(
  order: readonly T[],
  copy: Record<T, Copy>,
): readonly StyleOption<T>[] => order.map((value) => ({ value, ...copy[value] }));

const ERROR_CORRECTION_COPY: Record<ErrorCorrectionLevel, Copy> = {
  L: { label: 'Niedrig (7 %)', description: 'Minimale Fehlerkorrektur' },
  M: { label: 'Mittel (15 %)', description: 'Standard-Fehlerkorrektur' },
  Q: { label: 'Quartil (25 %)', description: 'Gute Fehlerkorrektur für Anpassungen' },
  H: { label: 'Hoch (30 %)', description: 'Maximale Fehlerkorrektur, ideal für Logos' },
};

const MODULE_STYLE_COPY: Record<ModuleStyle, Copy> = {
  classic: { label: 'Klassisch', description: 'Standard QR-Code mit quadratischen Modulen' },
  dotted: { label: 'Gepunktet', description: 'Kleine Punkte statt Quadrate' },
  rounded: { label: 'Abgerundet', description: 'Module mit stark abgerundeten Ecken' },
  rounded_rect: {
    label: 'Abgerundete Rechtecke',
    description: 'Module mit leicht abgerundeten Ecken',
  },
  circle: { label: 'Kreise', description: 'Kreisförmige Module' },
  bubbles: { label: 'Blasen', description: 'Kreise mit unterschiedlichen Größen' },
  diamond: { label: 'Rauten', description: 'Rautenförmige Module' },
  classy: { label: 'Elegant', description: 'Kleinere Quadrate mit Abstand' },
  classy_rounded: {
    label: 'Elegant abgerundet',
    description: 'Kleinere Quadrate mit runden Ecken',
  },
  hexagon: { label: 'Sechsecke', description: 'Sechseckige Module für ein modernes Design' },
  star: { label: 'Sterne', description: 'Sternförmige Module' },
  heart: { label: 'Herzen', description: 'Herzförmige Module' },
  leaf: { label: 'Blätter', description: 'Blattförmige Module' },
  triangle: { label: 'Dreiecke', description: 'Dreieckige Module' },
  cross: { label: 'Kreuze', description: 'Kreuzförmige Module' },
  flower: { label: 'Blüten', description: 'Blütenförmige Module' },
  lightning: { label: 'Blitze', description: 'Blitzförmige Module' },
  waves: { label: 'Wellen', description: 'Wellenförmige Module' },
};

const OUTER_EYE_COPY: Record<OuterEyeStyle, Copy> = {
  square: { label: 'Quadratisch', description: 'Klassische quadratische Ecken' },
  rounded: { label: 'Abgerundet', description: 'Abgerundete Ecken — Stärke einstellbar' },
  circle: { label: 'Kreisförmig', description: 'Kreisförmige Ecken' },
  rounded_top_left: { label: 'Oben links abgerundet', description: 'Nur die obere linke Ecke' },
  rounded_top_right: { label: 'Oben rechts abgerundet', description: 'Nur die obere rechte Ecke' },
  rounded_bottom_left: {
    label: 'Unten links abgerundet',
    description: 'Nur die untere linke Ecke',
  },
  rounded_bottom_right: {
    label: 'Unten rechts abgerundet',
    description: 'Nur die untere rechte Ecke',
  },
  diagonal: { label: 'Diagonal', description: 'Abgeschrägte gegenüberliegende Ecken' },
  dots: { label: 'Punkte', description: 'Rand aus einzelnen Punkten' },
  diamond: { label: 'Raute', description: 'Rautenförmige Ecken' },
  flower_shape: { label: 'Blütenform', description: 'Blütenförmige Ecken' },
  star_shape: { label: 'Sternform', description: 'Sternförmige Ecken' },
  hexagon: { label: 'Sechseck', description: 'Sechseckige Ecken' },
  shield: { label: 'Schild', description: 'Schildförmige Ecken' },
  wave: { label: 'Welle', description: 'Wellenförmige Ecken' },
  gear: { label: 'Zahnrad', description: 'Zahnradförmige Ecken' },
};

const INNER_EYE_COPY: Record<InnerEyeStyle, Copy> = {
  square: { label: 'Quadratisch', description: 'Klassisches quadratisches Zentrum' },
  rounded: { label: 'Abgerundet', description: 'Abgerundetes Zentrum — Stärke einstellbar' },
  circle: { label: 'Kreisförmig', description: 'Kreisförmiges Zentrum' },
  dots_3x3: { label: '3×3 Punkte', description: 'Zentrum aus neun Punkten' },
  diamond: { label: 'Raute', description: 'Rautenförmiges Zentrum' },
  leaf: { label: 'Blatt', description: 'Blattförmiges Zentrum' },
  vertical_bars: { label: 'Vertikale Balken', description: 'Drei senkrechte Balken' },
  horizontal_bars: { label: 'Horizontale Balken', description: 'Drei waagerechte Balken' },
  star: { label: 'Stern', description: 'Sternförmiges Zentrum' },
  heart: { label: 'Herz', description: 'Herzförmiges Zentrum' },
  flower: { label: 'Blüte', description: 'Blütenförmiges Zentrum' },
  cross: { label: 'Kreuz', description: 'Kreuzförmiges Zentrum' },
  triangle: { label: 'Dreieck', description: 'Dreieckiges Zentrum' },
  hexagon: { label: 'Sechseck', description: 'Sechseckiges Zentrum' },
  wave: { label: 'Welle', description: 'Wellenförmiges Zentrum' },
  lightning: { label: 'Blitz', description: 'Blitzförmiges Zentrum' },
  target: { label: 'Zielscheibe', description: 'Ring mit Punkt in der Mitte' },
};

export const ERROR_CORRECTION_OPTIONS = toOptions(ERROR_CORRECTION_LEVELS, ERROR_CORRECTION_COPY);
export const MODULE_STYLE_OPTIONS = toOptions(MODULE_STYLES, MODULE_STYLE_COPY);
export const OUTER_EYE_OPTIONS = toOptions(OUTER_EYE_STYLES, OUTER_EYE_COPY);
export const INNER_EYE_OPTIONS = toOptions(INNER_EYE_STYLES, INNER_EYE_COPY);

/** Styles whose rounding slider actually does something. */
export const OUTER_EYE_STYLES_WITH_RADIUS: readonly OuterEyeStyle[] = [
  'rounded',
  'rounded_top_left',
  'rounded_top_right',
  'rounded_bottom_left',
  'rounded_bottom_right',
];

export const INNER_EYE_STYLES_WITH_RADIUS: readonly InnerEyeStyle[] = ['rounded'];

export const QR_CODE_SIZES = [
  { value: 200, label: 'Klein (200 px)' },
  { value: 300, label: 'Mittel (300 px)' },
  { value: 400, label: 'Groß (400 px)' },
  { value: 500, label: 'Sehr groß (500 px)' },
  { value: 800, label: 'Druckqualität (800 px)' },
] as const;

export const QR_CODE_TIPS: readonly string[] = [
  'Verwende eine hohe Fehlerkorrektur (H), wenn du ein Logo einsetzt.',
  'Halte den Kontrast zwischen Modul- und Hintergrundfarbe hoch — helle Module auf hellem Grund sind nicht lesbar.',
  'Das Logo sollte höchstens 30 % des QR-Codes einnehmen.',
  'Der Ruhebereich am Rand wird automatisch eingefügt und ist für viele Scanner erforderlich.',
  'Lade den Code als SVG herunter, wenn du ihn drucken oder skalieren willst.',
  'Teste den fertigen Code mit dem eingebauten Scanner, bevor du ihn verwendest.',
];
